import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { toast } from 'sonner';

import { ShelfRequestError } from '@/shared/api/lotus';
import type { DocumentDraft, DocumentDraftStore } from '@/entities/document-draft';
import { getDocumentDraftKey, replaceDraft, type DraftRef } from './documentDraftPersistence';
import type { DocumentEditorSaveState } from './documentEditorTypes';
import { useDocumentDraftSaveQueue } from './useDocumentDraftSaveQueue';

export function isDocumentDraftLicenseError(error: unknown): boolean {
  return error instanceof ShelfRequestError && error.status === 403;
}

function notifyDocumentDraftSaveError(error: unknown): void {
  if (isDocumentDraftLicenseError(error)) {
    toast.error('Draft could not be saved.', {
      description: 'You must be licensed for this patient to edit drafts.',
    });
    return;
  }

  toast.error('Draft could not be saved.', {
    description: 'Please check your connection and try again.',
  });
}

interface UseDocumentDraftPersistenceProps {
  patientId: string;
  store: DocumentDraftStore;
  loadKey: string;
  isNewDraft: boolean;
  hasLoadError: boolean;
  requiredDataLoading: boolean;
  loadedDraft: DocumentDraft | null;
  draft: DocumentDraft | null;
  draftOverride: DocumentDraft | null;
  draftRef: DraftRef;
  setSaveState: Dispatch<SetStateAction<DocumentEditorSaveState>>;
}

export function useDocumentDraftPersistence({
  patientId,
  store,
  loadKey,
  isNewDraft,
  hasLoadError,
  requiredDataLoading,
  loadedDraft,
  draft,
  draftOverride,
  draftRef,
  setSaveState,
}: UseDocumentDraftPersistenceProps): void {
  const { enqueueDraftSave, getLatestDrafts } = useDocumentDraftSaveQueue({ patientId, store });
  const createdDraftKeyRef = useRef<string | null>(null);
  const draftContentKey = draft === null ? null : getDocumentDraftKey(draft);
  const canAutosave = draft !== null && loadedDraft !== null && draftOverride !== null;

  useEffect(() => {
    if (
      requiredDataLoading ||
      hasLoadError ||
      loadedDraft === null ||
      !isNewDraft ||
      createdDraftKeyRef.current === loadKey
    ) {
      return;
    }
    setSaveState('saving');
    enqueueDraftSave(replaceDraft(getLatestDrafts(), loadedDraft), {
      onSuccess: () => {
        createdDraftKeyRef.current = loadKey;
        setSaveState('saved');
      },
      onError: (error) => {
        setSaveState('error');
        notifyDocumentDraftSaveError(error);
      },
    });
  }, [
    enqueueDraftSave,
    getLatestDrafts,
    hasLoadError,
    isNewDraft,
    loadKey,
    loadedDraft,
    requiredDataLoading,
    setSaveState,
  ]);

  useEffect(() => {
    if (!canAutosave || draftContentKey === null) {
      return;
    }
    const timer = window.setTimeout(() => {
      const draftToSave = draftRef.current;
      if (draftToSave === null || getDocumentDraftKey(draftToSave) !== draftContentKey) {
        return;
      }

      enqueueDraftSave(replaceDraft(getLatestDrafts(), draftToSave), {
        onSuccess: () => {
          if (
            draftRef.current !== null &&
            getDocumentDraftKey(draftRef.current) === draftContentKey
          ) {
            if (isNewDraft) {
              createdDraftKeyRef.current = loadKey;
            }
            setSaveState('saved');
          }
        },
        onError: (error) => {
          setSaveState('error');
          notifyDocumentDraftSaveError(error);
        },
      });
    }, 700);
    return () => window.clearTimeout(timer);
  }, [
    canAutosave,
    draftContentKey,
    draftRef,
    enqueueDraftSave,
    getLatestDrafts,
    isNewDraft,
    loadKey,
    setSaveState,
  ]);
}
